import { createEntityAdapter, createSlice, createAction, createReducer } from '@reduxjs/toolkit';

import { useAppDispatch, useAppSelector } from '@shared/hooks';
import type { ScheduleAddress } from '@shared/types/schedules';

const addressAdapter = createEntityAdapter<ScheduleAddress>();

const addressSlice = createSlice({
	name: 'schedules/address',
	initialState: addressAdapter.getInitialState(),
	reducers: {
		addressesSet: addressAdapter.setAll,
		addressRemove: addressAdapter.removeOne,
		addressAdd: addressAdapter.addOne,
	},
});

export const addressReducer = addressSlice.reducer;
export const addressActions = addressSlice.actions;

const addressSelectors = addressAdapter.getSelectors();

interface SelectedAddressState {
	id: number | null;
}

const selectAddressById = createAction<number | null>('schedules/selectAddress');

const initialState = {
	id: null,
} as SelectedAddressState;

export const selectAddressReducer = createReducer(initialState, (builder) => {
	builder.addCase(selectAddressById, (state, action) => {
		state.id = action.payload;
	});
});

const activeAddressSelector = (state: RootState) => state.schedule.selectedAddress.id;

export const useAddresses = () => {
	const dispatch = useAppDispatch();

	const addresses = useAppSelector((state) =>
		addressSelectors.selectAll(state.schedule.addresses)
	).sort((a, b) => Number(a.order) - Number(b.order));

	const activeAddressId = useAppSelector((state) => activeAddressSelector(state));

	const activeAddress = addresses.find((addr) => addr.id === activeAddressId);

	const setAddresses = (addresses: ScheduleAddress[]) =>
		dispatch(addressActions.addressesSet(addresses));

	const removeAddress = (id: number) => dispatch(addressActions.addressRemove(id));

	const addAddress = (address: ScheduleAddress) => dispatch(addressActions.addressAdd(address));

	const selectAddress = (id: number | null) => dispatch(selectAddressById(id));

	return {
		addresses,
		activeAddressId,
		activeAddress,
		setAddresses,
		removeAddress,
		addAddress,
		selectAddress,
	};
};

export const useAddressById = (id: number) => {
	const dispatch = useAppDispatch();
	const removeAddress = () => dispatch(addressActions.addressRemove(id));

	const address = useAppSelector((state) =>
		addressSelectors.selectById(state.schedule.addresses, id)
	);
	return { address, removeAddress };
};
